import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/payFileRemoveInvoice.mutation"

import type {
  RemoveInvoiceFromPayFileFailure,
  RemoveInvoiceFromPayFileInput,
  RemoveInvoiceFromPayFileMutation,
  RemoveInvoiceFromPayFileMutationVariables,
} from "@/graphql/codegen/graphql"
import { RemoveInvoiceFromPayFileDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export interface UseRemovePayFileInvoiceReturn {
  removePayFileInvoice: (input: RemoveInvoiceFromPayFileInput) => Promise<boolean>
  isRemoving: boolean
}

export const useRemovePayFileInvoice = (): UseRemovePayFileInvoiceReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<
    RemoveInvoiceFromPayFileMutation,
    RemoveInvoiceFromPayFileMutationVariables
  >(RemoveInvoiceFromPayFileDocument)

  const removePayFileInvoice = useCallback(
    async (input: RemoveInvoiceFromPayFileInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.payFile.removeInvoice
      const hasError = error || output?.__typename === "RemoveInvoiceFromPayFileFailure"

      if (hasError) {
        const message = error?.message ?? (output as RemoveInvoiceFromPayFileFailure).error.message
        notify({ type: NotificationType.Error, message })
        return false
      }

      notify({ type: NotificationType.Success, message: "Invoice removed" })
      return true
    },
    [executeMutation, notify]
  )

  return { removePayFileInvoice, isRemoving: fetching }
}
